export const DEFAULT_PLATFORM_TIMEZONE = 'Asia/Kolkata';

let platformTimezone = DEFAULT_PLATFORM_TIMEZONE;

export function getPlatformTimezone(): string {
  return platformTimezone;
}

export function setPlatformTimezone(timezone: string): void {
  if (timezone?.trim()) {
    platformTimezone = timezone.trim();
  }
}

/**
 * Parse API date values.
 * Timezone-less LocalDateTime strings from the backend are treated as UTC so IST
 * (and other) users do not see brand-new events as ~5 hours old.
 */
export function parseApiDate(value?: string | Date | null): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  if (/[zZ]$/.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    const dated = new Date(raw);
    return Number.isNaN(dated.getTime()) ? null : dated;
  }

  let normalized = raw.includes('T') ? raw : raw.replace(' ', 'T');
  normalized = normalized.replace(/(\.\d{1,9})$/, '');

  const asUtc = new Date(`${normalized}Z`);
  return Number.isNaN(asUtc.getTime()) ? null : asUtc;
}

function toDate(value?: string | Date | null): Date | null {
  return parseApiDate(value);
}

export function formatPlatformDate(
  value?: string | Date | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: platformTimezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  }).format(date);
}

export function formatPlatformDateTime(
  value?: string | Date | null,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: platformTimezone,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...options,
  }).format(date);
}

export function formatRelativeTime(value?: string | Date | null): string {
  const date = toDate(value);
  if (!date) return '';

  const diffMs = Date.now() - date.getTime();
  const diffInMinutes = Math.floor(diffMs / (1000 * 60));
  const diffInHours = diffMs / (1000 * 60 * 60);

  if (diffInMinutes <= 1) return 'Just now';
  if (diffInHours < 1) return `${diffInMinutes} minutes ago`;
  if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }
  if (diffInHours < 168) {
    const days = Math.floor(diffInHours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }

  return formatPlatformDate(date, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
}
